import {
  FACEBOOK,
  INSTAGRAM,
  LINKEDIN,
  LINKEDIN_PUBLISH_AS,
  TIKTOK,
  YOUTUBE,
} from "./constants";

const extractLinkedInOrgIdFromReference = (reference = "") => {
  if (!reference) return "";
  const match = reference.match(/urn:li:organization:(.+)/);
  return match ? match[1] : "";
};

const buildLinkedInOrganizationOptionsFromAdvertisers = ({
  integration,
  advertisers = [],
}) => {
  const groupedAdvertisers = new Map();

  advertisers.forEach((advertiser) => {
    const reference = advertiser.reference || "";
    const pageName = advertiser.pageName || "";
    const key = `${reference}-${pageName}`;

    if (!groupedAdvertisers.has(key)) {
      groupedAdvertisers.set(key, []);
    }
    groupedAdvertisers.get(key).push(advertiser);
  });

  const organizationAccounts = [];

  groupedAdvertisers.forEach((advertiserGroup) => {
    const firstAdvertiser = advertiserGroup[0];
    const reference = firstAdvertiser.reference || "";
    const orgUrn = extractLinkedInOrgIdFromReference(reference);
    const pageName = firstAdvertiser.pageName || "";
    const displayName = pageName || orgUrn;

    organizationAccounts.push({
      id: orgUrn,
      name: pageName || orgUrn,
      integrationID: integration.id,
      publishAs: LINKEDIN_PUBLISH_AS.ORGANIZATION,
      orgUrn,
      displayLabel: displayName,
      advertiserIds: advertiserGroup.map((adv) => adv.id),
    });
  });

  return organizationAccounts;
};

const getLinkedInPublishingAccounts = (linkedInIntegrations = {}) =>
  Object.values(linkedInIntegrations).flatMap((integration) => {
    const accounts = [];

    accounts.push({
      id: integration.id,
      name: integration?.name,
      integrationID: integration.id,
      publishAs: LINKEDIN_PUBLISH_AS.PROFILE,
      isPersonal: true,
      displayLabel: integration?.name || "",
    });

    const pages = Object.values(integration?.pages || {});

    if (pages.length > 0) {
      pages.forEach((page) => {
        const orgId = String(page.id);
        const organizationUrn =
          page.organizationUrn || `urn:li:organization:${orgId}`;
        const orgUrn = extractLinkedInOrgIdFromReference(organizationUrn);
        const matchingAdvertisers = Object.values(
          integration?.advertisers || {}
        ).filter(
          (advertiser) =>
            advertiser?.reference === organizationUrn ||
            extractLinkedInOrgIdFromReference(advertiser?.reference) === orgId
        );

        accounts.push({
          id: orgId,
          name: page.name || orgId,
          integrationID: integration.id,
          publishAs: LINKEDIN_PUBLISH_AS.ORGANIZATION,
          orgUrn,
          organizationUrn,
          displayLabel: page.name || orgId,
          isPage: true,
          pageId: orgId,
          advertiserIds: matchingAdvertisers.map((advertiser) => advertiser.id),
        });
      });
    } else {
      accounts.push(
        ...buildLinkedInOrganizationOptionsFromAdvertisers({
          integration,
          advertisers: Object.values(integration?.advertisers || {}),
        })
      );
    }

    return accounts;
  });

const formatInstagramHandle = (username = "") => {
  const trimmed = username.trim();
  if (!trimmed) return "";
  return trimmed.startsWith("@") ? trimmed : `@${trimmed}`;
};

const buildAccountEntry = ({ platform, accountId, handle, account }) => ({
  key: `${platform}:${accountId}`,
  platform,
  handle,
  account,
});

export const buildSharePlatformAccounts = ({
  integrations = {},
  uniqueInstagramBusinessList = [],
  uniqueFacebookPagesList = [],
}) => {
  const accounts = [];

  uniqueInstagramBusinessList.forEach((account) => {
    if (!account?.instagramID) return;

    accounts.push(
      buildAccountEntry({
        platform: INSTAGRAM,
        accountId: account.instagramID,
        handle: formatInstagramHandle(account.name),
        account,
      })
    );
  });

  uniqueFacebookPagesList.forEach((account) => {
    if (!account?.id) return;

    accounts.push(
      buildAccountEntry({
        platform: FACEBOOK,
        accountId: account.id,
        handle: account.name || account.id,
        account,
      })
    );
  });

  Object.entries(integrations?.tiktok || {}).forEach(
    ([integrationID, integration]) => {
      if (!integration?.name) return;

      accounts.push(
        buildAccountEntry({
          platform: TIKTOK,
          accountId: integrationID,
          handle: integration.name,
          account: {
            ...integration,
            integrationID,
          },
        })
      );
    }
  );

  Object.entries(integrations?.youtube || {}).forEach(([, integration]) => {
    if (integration?.isActive !== true) return;

    const accountId = integration.id || integration.channelID;
    if (!accountId) return;

    accounts.push(
      buildAccountEntry({
        platform: YOUTUBE,
        accountId,
        handle:
          integration.channelTitle || integration.channelID || integration.id,
        account: {
          ...integration,
          integrationID: integration.id,
          channelID: integration.channelID,
        },
      })
    );
  });

  getLinkedInPublishingAccounts(integrations?.linkedIn).forEach((account) => {
    if (!account?.id) return;

    accounts.push(
      buildAccountEntry({
        platform: LINKEDIN,
        accountId: account.id,
        handle: account.displayLabel || account.name || account.id,
        account,
      })
    );
  });

  return accounts;
};

export const getSelectedAccountKeys = (platformSelections = {}) =>
  Object.entries(platformSelections)
    .filter(([, enabled]) => enabled)
    .map(([accountKey]) => accountKey)
    .filter(Boolean);

export const parseAccountKey = (accountKey = "") => {
  const separatorIndex = String(accountKey).indexOf(":");
  if (separatorIndex <= 0) {
    return { platform: "", accountID: "" };
  }
  return {
    platform: accountKey.slice(0, separatorIndex),
    accountID: accountKey.slice(separatorIndex + 1),
  };
};

export const getSelectedPlatformIds = (platformSelections = {}) => [
  ...new Set(
    getSelectedAccountKeys(platformSelections)
      .map((accountKey) => parseAccountKey(accountKey).platform)
      .filter(Boolean)
  ),
];

export const buildDefaultPlatformSelections = (accounts = []) =>
  accounts.reduce((selections, account) => {
    selections[account.key] = true;
    return selections;
  }, {});
